import { MoreHorizontal } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const STATUS_DISPLAY = {
  nouveau: { label: "Nouveau", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  confirme: { label: "Confirmé", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  annule: { label: "Annulé", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  termine: { label: "Terminé", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  a_completer: { label: "À compléter", cls: "bg-purple-100 text-purple-800 border-purple-200" },
};

export const rdvDisplayStatus = (r, today, nowMin) => {
  if (r.rdv_status === "annule" || r.booking_status === "annule") return "annule";
  if (["a_reconfirmer", "annulation_demandee"].includes(r.booking_status)) return "en_attente";
  const past = r.date < today || (r.date === today && r.start_min + (r.duration_minutes || 60) <= nowMin);
  if (past || r.rdv_status === "termine") {
    return (r.report || "").trim() || ["finalise", "envoye"].includes(r.report_status) ? "termine" : "a_completer";
  }
  if (r.rdv_status === "confirme" || r.booking_status === "confirme") return "confirme";
  return "nouveau";
};

export const StatusBadge = ({ status }) => {
  const b = STATUS_DISPLAY[status] || STATUS_DISPLAY.nouveau;
  return <span className={`text-[11px] font-medium border rounded-full px-2 py-0.5 whitespace-nowrap ${b.cls}`}
    data-testid={`rdv-status-${status}`}>{b.label}</span>;
};

export const toHHMM = (m) => `${String(Math.max(0, Math.floor(m / 60))).padStart(2, "0")}:${String(Math.max(0, m % 60)).padStart(2, "0")}`;
export const fmtShortDate = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" });

export default function RdvTable({ title, subtitle, count, columns, rows, onRowClick, actions, headerAction, testid, emptyText }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-[0_1px_3px_rgba(0,0,0,0.04)] overflow-hidden flex flex-col" data-testid={testid}>
      <div className="flex items-center gap-3 px-5 pt-4 pb-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            {title}
            <span className="text-[11px] font-medium text-slate-500 bg-slate-100 rounded-full px-2 py-0.5" data-testid={`${testid}-count`}>{count}</span>
          </h3>
          <p className="text-xs text-slate-400 mt-0.5">{subtitle}</p>
        </div>
        {headerAction}
      </div>
      <div className="overflow-x-auto flex-1">
        <table className="w-full min-w-[520px] text-left">
          <thead>
            <tr className="border-y border-slate-100 bg-slate-50/60">
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-2 text-[11px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{c.label}</th>
              ))}
              {actions && <th className="w-10" />}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={columns.length + (actions ? 1 : 0)}
                className="px-5 py-8 text-center text-sm text-slate-400" data-testid={`${testid}-empty`}>{emptyText}</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} onClick={() => onRowClick?.(r)}
                className="border-b border-slate-50 last:border-0 hover:bg-slate-50/70 cursor-pointer transition-colors"
                data-testid={`${testid}-row-${r.id}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2.5 text-sm text-slate-700 align-top">{c.render(r)}</td>
                ))}
                {actions && (
                  <td className="px-2 align-top" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`${testid}-actions-${r.id}`}>
                          <MoreHorizontal className="w-4 h-4 text-slate-400" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        {actions(r).filter(Boolean).map((a) => (
                          <DropdownMenuItem key={a.label} onClick={a.onClick}
                            className={a.danger ? "text-rose-600 focus:text-rose-600" : ""}
                            data-testid={`${testid}-action-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                            {a.icon}{a.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
